/**
 * O mesmo `PrintDocument` numa folha A4 (F135).
 *
 * A bobina é o papel do balcão; o extrato que vai por e-mail, anexado ou
 * entregue num envelope sai numa folha. O documento é o MESMO dado — só o
 * renderizador muda, como a bobina e o ESC/POS já fazem com o recibo.
 *
 * - `text` grande vira título; em negrito, destaque;
 * - `pair` vira uma linha com o rótulo à esquerda e o valor à direita, com
 *   pontilhado entre os dois, que é como um extrato em papel se lê;
 * - `rule` vira uma linha fina; `feed`, espaço; `cut` no meio do documento vira
 *   uma página nova, e no fim dele não vira nada (F150);
 * - `table` vira uma `<table>` de verdade, com o cabeçalho repetido quando a
 *   tabela passa de uma folha e nenhuma linha partida ao meio (F170).
 */
const escapeHtml = (value) => value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
/**
 * De onde em diante só há corte. Ver `text.ts` — a regra é a mesma.
 *
 * Numa folha, um `cut` no meio é uma PÁGINA NOVA: é assim que duas vias de um
 * relatório saem em duas folhas. No fim do documento ele não é nada, e emitir
 * uma quebra ali daria uma folha em branco a cada extrato impresso.
 */
const trailingCutFrom = (blocks) => {
    let from = blocks.length;
    while (from > 0 && blocks[from - 1]?.kind === 'cut')
        from -= 1;
    return from;
};
const blockHtml = (block, midDocument = false) => {
    switch (block.kind) {
        case 'text': {
            const classes = [
                block.big === true ? 'big' : '',
                block.bold === true ? 'bold' : '',
                block.align ?? 'left',
            ]
                .filter((name) => name.length > 0)
                .join(' ');
            return `<p class="${classes}">${escapeHtml(block.text)}</p>`;
        }
        case 'pair':
            return `<div class="pair${block.bold === true ? ' bold' : ''}"><span>${escapeHtml(block.label.trim())}</span><span class="dots"></span><span class="value">${escapeHtml(block.value)}</span></div>`;
        case 'rule':
            return '<hr>';
        case 'feed':
            return `<div style="height:${String(block.lines * 0.6)}em"></div>`;
        case 'barcode':
            return `<p class="center mono">${escapeHtml(block.data)}</p>`;
        case 'cut':
            return midDocument ? '<div class="page-break"></div>' : '';
        case 'table': {
            const cell = (tag, text, index) => {
                const align = block.columns[index]?.align ?? 'left';
                return `<${tag} class="${align}">${escapeHtml(text)}</${tag}>`;
            };
            const head = block.columns
                .map((column, index) => cell('th', column.label, index))
                .join('');
            const rows = block.rows
                .map((row) => `<tr>${row.map((text, index) => cell('td', text, index)).join('')}</tr>`)
                .join('');
            const foot = block.footer === undefined
                ? ''
                : `<tfoot><tr>${block.footer.map((text, index) => cell('td', text, index)).join('')}</tr></tfoot>`;
            return `<table class="grid"><thead><tr>${head}</tr></thead><tbody>${rows}</tbody>${foot}</table>`;
        }
    }
};
export const renderDocumentA4Html = (document) => {
    /*
     * Tudo preto — F211. As linhas e bordas eram #999 e o cabeçalho da tabela
     * tinha fundo #eee: na impressora da loja, cinza vira quase nada.
     */
    const css = `
  @page { size: A4; margin: 18mm 16mm }
  html, body { margin: 0; padding: 0; background: #fff; color: #000 }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 11pt; line-height: 1.35; font-weight: 500 }
  main { max-width: 178mm; margin: 0 auto }
  p { margin: 0 0 1.5mm }
  .big { font-size: 15pt }
  .bold { font-weight: 700 }
  .left { text-align: left }
  .center { text-align: center }
  .right { text-align: right }
  .mono { font-family: "Courier New", Courier, monospace }
  hr { border: 0; border-top: 0.3mm solid #000; margin: 3mm 0 }
  .pair { display: flex; align-items: baseline; gap: 2mm; margin: 0 0 1mm }
  .pair .dots { flex: 1; border-bottom: 0.3mm dotted #000; transform: translateY(-1mm) }
  .pair .value { font-variant-numeric: tabular-nums; white-space: nowrap }
  .page-break { break-after: page; page-break-after: always; height: 0 }
  table.grid { width: 100%; border-collapse: collapse; margin: 0 0 3mm; font-variant-numeric: tabular-nums }
  table.grid thead { display: table-header-group }
  table.grid tr { break-inside: avoid; page-break-inside: avoid }
  table.grid th, table.grid td { border: 0.3mm solid #000; padding: 1mm 1.5mm }
  table.grid th { font-weight: 700 }
  table.grid tfoot td { font-weight: 700 }
  `;
    const tailFrom = trailingCutFrom(document.blocks);
    const body = document.blocks
        .map((block, index) => blockHtml(block, index < tailFrom))
        .join('\n');
    return `<!doctype html>
<html lang="pt-BR">
<head>
<meta charset="utf-8">
<title>${escapeHtml(document.title)}</title>
<style>${css}</style>
</head>
<body><main>
${body}
</main></body>
</html>`;
};
//# sourceMappingURL=a4-html.js.map